import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { BRAND } from "@/brand/brand";

/** The metas the page sets while it is open: [attribute, key, content]. */
type Meta = ["name" | "property", string, string];

/**
 * The tab title and the metas of the public onboarding page, set while it is open and put back when it is left.
 *
 * The brand comes from the host's brand (`BRAND.name`). Nothing person- or link-specific goes in: no token, no company,
 * no names - the address carries the token and a link preview reads the static shell anyway (scripts/brand-shells.js),
 * this is what a browser shows after the script has run. `robots` keeps the page out of every index and `referrer` keeps
 * the address (with the token in it) from travelling to the links on the page (imprint, privacy).
 */
export function useOnboardingHead(): void {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  useEffect(() => {
    const brand = BRAND.name;
    const title = t("onboardingPage.meta.title", { brand });
    const description = t("onboardingPage.meta.description", { brand });
    const metas: Meta[] = [
      ["name", "description", description],
      ["property", "og:title", t("onboardingPage.meta.ogTitle", { brand })],
      ["property", "og:description", description],
      ["property", "og:type", "website"],
      ["property", "og:site_name", brand],
      ["name", "twitter:card", "summary"],
      ["name", "robots", "noindex, nofollow, noarchive"],
      ["name", "referrer", "no-referrer"],
    ];
    const previousTitle = document.title;
    document.title = title;
    const undo = metas.map(([attribute, key, content]) => {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      const created = !element;
      const previous = element?.getAttribute("content") ?? null;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
      return () => {
        if (created) element?.remove();
        else if (previous !== null) element?.setAttribute("content", previous);
      };
    });
    return () => {
      document.title = previousTitle;
      undo.forEach((restore) => restore());
    };
  }, [t, language]);
}
